"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import type { PageTexts } from "@/lib/siteTexts";

// Wording comes from Admin -> Тексты страниц (homepage.why.*).
const featureStyles = [
  { icon: "BoltIcon", color: "#00D4FF" },
  { icon: "MapPinIcon", color: "#4F7CFF" },
  { icon: "ShieldCheckIcon", color: "#7C3AED" },
  { icon: "CodeBracketIcon", color: "#00FF88" },
  { icon: "CurrencyDollarIcon", color: "#FF6B6B" },
  { icon: "ArrowPathIcon", color: "#FFB800" },
] as const;

type Texts = PageTexts<"homepage">;

/** "99.95%" -> counts up to 99.95 with "%"; "24/7" -> 24 then "/7"; text without a number is shown as is. */
function parseStat(value: string): { prefix: string; target: number; decimals: number; suffix: string } | null {
  const m = value.trim().match(/^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/);
  if (!m) return null;
  const number = m[2].replace(",", ".");
  return { prefix: m[1], target: Number(number), decimals: number.split(".")[1]?.length ?? 0, suffix: m[3] };
}

function AnimatedCounter({ target, suffix = "", prefix = "", decimals = 0 }: {
  target: number; suffix?: string; prefix?: string; decimals?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const step = target / 60;
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(start);
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-4xl font-bold gradient-text">
      {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </div>
  );
}

export default function WhyUsSection({ texts: t }: { texts: Texts }) {
  const stats = ([1, 2, 3, 4] as const).map((n) => ({ value: t[`why.stat${n}.value`], label: t[`why.stat${n}.label`] }));
  const features = featureStyles.map((style, i) => {
    const n = (i + 1) as 1 | 2 | 3 | 4 | 5 | 6;
    return { ...style, title: t[`why.feature${n}.title`], description: t[`why.feature${n}.description`] };
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-[#0D1526] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[rgba(0,212,255,0.04)] blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {stats.map((stat, i) => {
            const parsed = parseStat(stat.value);
            return (
            <div
              key={i}
              className="glass rounded-2xl p-6 text-center neon-border-blue reveal"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {parsed ? (
                <AnimatedCounter
                  target={parsed.target}
                  suffix={parsed.suffix}
                  prefix={parsed.prefix}
                  decimals={parsed.decimals}
                />
              ) : (
                <div className="text-4xl font-bold gradient-text">{stat.value}</div>
              )}
              <div className="text-sm text-[#4A6080] mt-2">{stat.label}</div>
            </div>
            );
          })}
        </div>

        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label">{t["why.label"]}</span>
          <h2 className="font-display font-bold text-4xl lg:text-5xl text-white mt-3 mb-4">
            {t["why.title"]}{" "}
            <span className="gradient-text">{t["why.titleAccent"]}</span>
          </h2>
          <p className="text-[#8BA3CC] text-lg max-w-2xl mx-auto">
            {t["why.subtitle"]}
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="glass rounded-2xl p-7 card-hover reveal group"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                style={{ background: `${feature.color}15` }}
              >
                <Icon name={feature.icon} size={24} style={{ color: feature.color }} />
              </div>
              <h3 className="font-display font-bold text-lg text-white mb-2">{feature.title}</h3>
              <p className="text-[#8BA3CC] text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}